/**
 * Error rethrown by the HTTP interceptor. It keeps the flattened message that
 * callers already rely on (error.message) plus the HTTP status and the raw
 * backend body, so callers can branch on status or on a backend error code.
 */
export interface ApiError extends Error {
  status?: number;
  error?: unknown;
}

export function createApiError(message: string, status: number | undefined, body: unknown): ApiError {
  return Object.assign(new Error(message), { status, error: body });
}

export function getApiErrorStatus(err: unknown): number | undefined {
  const status = (err as ApiError | null)?.status;
  return typeof status === 'number' ? status : undefined;
}

/** The `code` field of a backend error body such as { code, message }. */
export function getErrorBodyCode(body: unknown): string | undefined {
  const code = isRecord(body) ? body['code'] : undefined;
  return typeof code === 'string' ? code : undefined;
}

/** The `message` field of a backend error body such as { code, message }. */
export function getErrorBodyMessage(body: unknown): string | undefined {
  const message = isRecord(body) ? body['message'] : undefined;
  return typeof message === 'string' ? message : undefined;
}

export function getApiErrorCode(err: unknown): string | undefined {
  return getErrorBodyCode((err as ApiError | null)?.error);
}

export function getApiErrorBodyMessage(err: unknown): string | undefined {
  return getErrorBodyMessage((err as ApiError | null)?.error);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
